import React, { useEffect, useState } from 'react';
import { usersApi } from '../services/api';
import { toast } from 'react-hot-toast';
import { UserCircleIcon, PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../stores/authStore';
import ws from '../services/wsService';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { DataTable } from '../components/common/DataTable';

interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  isOnline: boolean;
  department?: string;
  supportLine?: number;
  monthlySalary?: number | null;
  salaryCurrency?: string;
}

interface EditUserForm {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isActive: boolean;
  department: string;
  supportLine: number;
  monthlySalary: string;
  salaryCurrency: string;
}

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState<EditUserForm>({
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    isActive: true,
    department: '',
    supportLine: 1,
    monthlySalary: '',
    salaryCurrency: 'RUB'
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const { user: currentUser } = useAuthStore();

  useEffect(() => {
    fetchUsers();
  }, [currentUser]);

  // Live online/offline dots: the backend broadcasts operator_status over WS
  // whenever an operator's first WS session opens or last one closes.
  useEffect(() => {
    const onStatus = (payload: { userId?: string; isOnline?: boolean }) => {
      if (!payload?.userId) return;
      setUsers((prev) =>
        prev.map((u) =>
          u.id === payload.userId ? { ...u, isOnline: !!payload.isOnline } : u
        )
      );
    };
    ws.on('operator_status', onStatus);
    return () => ws.off('operator_status', onStatus);
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await usersApi.getAll();
      const usersData = response.data?.data?.users || response.data?.users || response.data || [];
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch (error) {
      toast.error('Ошибка загрузки пользователей');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (user: User) => {
    setEditingUser(user);
    setEditForm({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: user.role,
      isActive: user.isActive,
      department: user.department || '',
      supportLine: user.supportLine || 1,
      monthlySalary: user.monthlySalary != null ? String(user.monthlySalary) : '',
      salaryCurrency: user.salaryCurrency || 'RUB'
    });
    setShowEditModal(true);
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    try {
      setActionLoading(true);
      await usersApi.update(editingUser.id, {
        ...editForm,
        monthlySalary: editForm.monthlySalary === '' ? null : editForm.monthlySalary,
      });
      toast.success('Пользователь успешно обновлен');
      setShowEditModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error('Ошибка при обновлении пользователя');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      setActionLoading(true);
      await usersApi.delete(userToDelete.id);
      toast.success('Пользователь успешно удален');
      setShowDeleteModal(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Ошибка при удалении пользователя');
    } finally {
      setActionLoading(false);
    }
  };

  const getRoleBadge = (role: string) => {
    const labels = {
      operator: 'Оператор',
      admin: 'Администратор',
      superadmin: 'Суперадмин',
    };

    const variantMap: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      operator: 'default',
      admin: 'secondary',
      superadmin: 'destructive',
    };

    return (
      <Badge variant={variantMap[role] || 'outline'}>
        {labels[role as keyof typeof labels] || role}
      </Badge>
    );
  };

  const getStatusBadge = (isActive: boolean, isOnline: boolean) => {
    if (!isActive) {
      return <Badge variant="destructive">Неактивен</Badge>;
    }
    if (isOnline) {
      return <Badge variant="default" className="bg-success hover:bg-success/90">Онлайн</Badge>;
    }
    return <Badge variant="outline">Оффлайн</Badge>;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Пользователи</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Управление пользователями системы
        </p>
      </div>

      {/* Self-registered accounts land here inactive; an admin must switch
          «Активен» on in the edit dialog before they can log in. */}
      {users.some(u => !u.isActive) && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-foreground">
          Неактивных аккаунтов: <span className="font-semibold">{users.filter(u => !u.isActive).length}</span>.
          Новые регистрации ждут активации — откройте пользователя и включите «Активен».
        </div>
      )}

      <div className="bg-card border border-border overflow-hidden">
        <DataTable
          data={users}
          loading={loading}
          emptyMessage="В системе пока нет пользователей или у вас нет прав для их просмотра."
          columns={[
            {
              key: 'user',
              header: 'Пользователь',
              className: 'px-3 py-2',
              cell: (user) => (
                <div className="flex items-center space-x-3">
                  <UserCircleIcon className="h-8 w-8 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground">
                      {user.firstName} {user.lastName}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      @{user.username}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {user.email}
                    </div>
                  </div>
                </div>
              )
            },
            {
              key: 'role',
              header: 'Роль',
              className: 'px-3 py-2',
              cell: (user) => getRoleBadge(user.role)
            },
            {
              key: 'status',
              header: 'Статус',
              className: 'px-3 py-2',
              cell: (user) => getStatusBadge(user.isActive, user.isOnline)
            },
            {
              key: 'department',
              header: 'Отдел',
              className: 'px-3 py-2 hidden md:table-cell',
              cell: (user) => (
                user.department ? (
                  <span className="text-sm text-foreground">{user.department}</span>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )
              )
            },
            {
              key: 'supportLine',
              header: 'Линия',
              className: 'px-3 py-2 hidden lg:table-cell',
              cell: (user) => (
                user.role === 'operator' ? (
                  <Badge variant={user.supportLine === 2 ? 'secondary' : 'outline'}>
                    {user.supportLine === 2 ? '2-я линия' : '1-я линия'}
                  </Badge>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )
              )
            },
            {
              key: 'actions',
              header: 'Действия',
              className: 'px-3 py-2 text-right',
              cell: (user) => (
                <div className="flex items-center justify-end space-x-1" onClick={(e) => e.stopPropagation()}>
                  {(currentUser?.role === 'admin' || currentUser?.role === 'superadmin') && (
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      onClick={() => handleEditClick(user)}
                      title="Редактировать"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </Button>
                  )}
                  {currentUser?.role === 'superadmin' && (
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      onClick={() => handleDeleteClick(user)}
                      title="Удалить"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )
            }
          ]}
        />
      </div>

      {/* Модальное окно редактирования */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-foreground">
                Редактировать пользователя
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowEditModal(false)}
              >
                <XMarkIcon className="h-6 w-6" />
              </Button>
            </div>
            
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground">
                  Имя
                </label>
                <Input
                  type="text"
                  value={editForm.firstName}
                  onChange={(e) => setEditForm({...editForm, firstName: e.target.value})}
                  className="mt-1"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground">
                  Фамилия
                </label>
                <Input
                  type="text"
                  value={editForm.lastName}
                  onChange={(e) => setEditForm({...editForm, lastName: e.target.value})}
                  className="mt-1"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground">
                  Email
                </label>
                <Input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                  className="mt-1"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground">
                  Роль
                </label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({...editForm, role: e.target.value})}
                  className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  required
                >
                  <option value="operator">Оператор</option>
                  <option value="admin">Администратор</option>
                  <option value="superadmin">Суперадмин</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground">
                  Отдел
                </label>
                <Input
                  type="text"
                  value={editForm.department}
                  onChange={(e) => setEditForm({...editForm, department: e.target.value})}
                  className="mt-1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground">
                  Оклад / мес (для финансов)
                </label>
                <div className="mt-1 flex gap-2">
                  <Input
                    type="number"
                    value={editForm.monthlySalary}
                    onChange={(e) => setEditForm({ ...editForm, monthlySalary: e.target.value })}
                    placeholder="напр. 45000"
                    className="flex-1"
                  />
                  <select
                    value={editForm.salaryCurrency}
                    onChange={(e) => setEditForm({ ...editForm, salaryCurrency: e.target.value })}
                    className="h-10 rounded-md border border-input bg-background px-2 text-sm"
                  >
                    {['RUB', 'KZT', 'USD', 'UZS', 'KGS', 'BYN', 'AMD', 'GEL'].map((x) => (
                      <option key={x} value={x}>{x}</option>
                    ))}
                  </select>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Падает в «Зарплаты» при генерации месяца в Финансах.
                </p>
              </div>

              {editForm.role === 'operator' && (
                <div>
                  <label className="block text-sm font-medium text-foreground">
                    Линия поддержки
                  </label>
                  <select
                    value={editForm.supportLine}
                    onChange={(e) => setEditForm({...editForm, supportLine: parseInt(e.target.value)})}
                    className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value={1}>1-я линия</option>
                    <option value={2}>2-я линия</option>
                  </select>
                </div>
              )}
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={editForm.isActive}
                  onChange={(e) => setEditForm({...editForm, isActive: e.target.checked})}
                  className="h-4 w-4 text-primary focus:ring-primary border-border rounded"
                />
                <label className="ml-2 block text-sm text-foreground">
                  Активен
                </label>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditModal(false)}
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Сохранение...' : 'Сохранить'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Модальное окно удаления */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-foreground">
                Подтверждение удаления
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowDeleteModal(false)}
              >
                <XMarkIcon className="h-6 w-6" />
              </Button>
            </div>
            
            <p className="text-foreground mb-6">
              Вы действительно хотите удалить пользователя <strong>{userToDelete.firstName} {userToDelete.lastName}</strong> (@{userToDelete.username})?
              <br /><br />
              <span className="text-destructive">Это действие нельзя отменить!</span>
            </p>
            
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(false)}
              >
                Отмена
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteConfirm}
                disabled={actionLoading}
              >
                {actionLoading ? 'Удаление...' : 'Удалить'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;